// Navigation helpers for navigating from queries to source documents
import { NavigateFunction } from "react-router-dom";
import { QueryEntity } from "../../domain/entities/QueryEntity";

export interface NavigationOptions {
  returnTo?: string;
  [key: string]: any;
}

/**
 * Navigate to the source document of a query
 * Supports: Invoice, Bill, Journal
 *
 * @param query - The query entity containing source information
 * @param navigate - React Router navigate function
 * @param options - Additional navigation options
 */
export const navigateToQuerySource = (
  query: QueryEntity,
  navigate: NavigateFunction,
  options?: NavigationOptions
): void => {
  if (!query.sourceId) {
    console.warn("Cannot navigate: Query has no source ID");
    return;
  }

  const defaultOptions: NavigationOptions = {
    returnTo: "queries",
    ...options,
  };

  switch (query.source) {
    case "invoice":
      navigate("/invoices/review", {
        state: {
          documentId: query.sourceId,
          ...defaultOptions,
          // Will fetch invoice details from API (no initial jsons needed)
        },
      });
      break;

    case "bill":
      navigate("/bills/review", {
        state: {
          documentId: query.sourceId,
          ...defaultOptions,
          // Will fetch bill details from API (no initial jsons needed)
        },
      });
      break;

    case "journal":
      navigate(`/journal/detail/${query.sourceId}`, {
        state: defaultOptions,
      });
      break;

    case "credit_note":
      navigate(`/credit/review/${query.sourceId}`, {
        state: {
          documentId: query.sourceId,
          ...defaultOptions,
        },
      });
      break;

    case "general":
      console.warn(
        "Cannot navigate: Query has no associated document (general query)"
      );
      break;

    default:
      console.warn(`Cannot navigate: Unknown source type "${query.source}"`);
  }
};

/**
 * Check if a query has a navigable source document
 */
export const hasNavigableSource = (query: QueryEntity): boolean => {
  return (
    query.source !== "general" &&
    query.sourceId !== null &&
    ["invoice", "bill", "journal", "credit_note"].includes(query.source)
  );
};

/**
 * Get the display name for a source type
 */
export const getSourceDisplayName = (source: string): string => {
  const displayNames: Record<string, string> = {
    general: "General",
    invoice: "Invoice",
    bill: "Bill",
    journal: "Journal",
    transaction: "Transaction",
    credit_note: "Credit Note",
  };

  return displayNames[source] || source;
};

/**
 * Get the navigation path for a source type
 */
export const getSourceNavigationPath = (
  source: string,
  sourceId?: number | null
): string | null => {
  if (!sourceId) return null;

  const paths: Record<string, string> = {
    invoice: "/invoices/review",
    bill: "/bills/review",
    journal: `/journal/detail/${sourceId}`,
    credit_note: `/credit/review/${sourceId}`,
  };

  return paths[source] || null;
};
